'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import Icon from '@/components/ui/AppIcon';
import { adminAuthService } from '@/lib/supabase/adminAuthService';
import { articleService } from '@/lib/supabase/articleService';
import { categoryService } from '@/lib/supabase/categoryService';
import { countryService } from '@/lib/supabase/countryService';

export default function AdminDashboard() {
  const router = useRouter();
  const { user, isAuthenticated, logout } = useAdminAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArticles: 0,
    totalCategories: 0,
    totalCountries: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      router?.push('/admin/login');
      return;
    }
    loadStats();
  }, [isAuthenticated, router]);

  const loadStats = async () => {
    try {
      // Load all stats in parallel
      const [users, articles, categories, countries] = await Promise.all([
        adminAuthService?.getAllAppUsers(),
        articleService?.getAll(),
        categoryService?.getAll(),
        countryService?.getAll(),
      ]);
      
      setStats({
        totalUsers: users?.length || 0,
        totalArticles: articles?.length || 0,
        totalCategories: categories?.length || 0,
        totalCountries: countries?.length || 0,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { icon: 'users', label: 'User Management', href: '/admin/users', description: 'Manage users and permissions', colorClass: 'blue' },
    { icon: 'document-text', label: 'Content Management', href: '/admin/articles', description: 'Manage articles and news', colorClass: 'green' },
    { icon: 'chart-bar', label: 'Analytics', href: '/admin/analytics', description: 'View statistics and reports', colorClass: 'purple' },
    { icon: 'folder', label: 'Categories', href: '/admin/categories', description: 'Manage news categories', colorClass: 'yellow' },
    { icon: 'globe-alt', label: 'Countries', href: '/admin/countries', description: 'Manage country settings', colorClass: 'indigo' },
    { icon: 'newspaper', label: 'Sources', href: '/admin/sources', description: 'Manage news sources', colorClass: 'pink' },
    { icon: 'shield-check', label: 'Moderation', href: '/admin/moderation', description: 'Content moderation', colorClass: 'red' },
    { icon: 'star', label: 'Featured', href: '/admin/featured', description: 'Manage featured content', colorClass: 'orange' },
    { icon: 'cog', label: 'Settings', href: '/admin/settings', description: 'System settings', colorClass: 'gray' },
    { icon: 'squares-2x2', label: 'Enhanced Dashboard', href: '/admin/dashboard', description: 'All-in-one management dashboard', colorClass: 'blue' },
  ];

  const trendingItems = [
    { title: 'User registrations increased by 45%', category: 'Users', trend: 'up', percentage: '+45%', icon: 'arrow-trending-up', color: 'green' },
    { title: 'New articles published today', category: 'Content', trend: 'up', percentage: '+12', icon: 'document-plus', color: 'blue' },
    { title: 'Most viewed category: Technology', category: 'Analytics', trend: 'hot', percentage: '2.4K', icon: 'fire', color: 'orange' },
    { title: 'Active moderation queue items', category: 'Moderation', trend: 'neutral', percentage: '8', icon: 'shield-exclamation', color: 'yellow' },
    { title: 'Global news coverage expanded', category: 'Countries', trend: 'up', percentage: '+3', icon: 'globe-americas', color: 'purple' },
  ];

  const getColorClasses = (colorClass: string) => {
    const colorMap: Record<string, { bg: string; icon: string; hover: string }> = {
      blue: { bg: 'bg-blue-100', icon: 'text-blue-600', hover: 'group-hover:bg-blue-200' },
      green: { bg: 'bg-green-100', icon: 'text-green-600', hover: 'group-hover:bg-green-200' },
      purple: { bg: 'bg-purple-100', icon: 'text-purple-600', hover: 'group-hover:bg-purple-200' },
      yellow: { bg: 'bg-yellow-100', icon: 'text-yellow-600', hover: 'group-hover:bg-yellow-200' },
      indigo: { bg: 'bg-indigo-100', icon: 'text-indigo-600', hover: 'group-hover:bg-indigo-200' },
      pink: { bg: 'bg-pink-100', icon: 'text-pink-600', hover: 'group-hover:bg-pink-200' },
      red: { bg: 'bg-red-100', icon: 'text-red-600', hover: 'group-hover:bg-red-200' },
      orange: { bg: 'bg-orange-100', icon: 'text-orange-600', hover: 'group-hover:bg-orange-200' },
      gray: { bg: 'bg-gray-100', icon: 'text-gray-600', hover: 'group-hover:bg-gray-200' },
    };
    return colorMap[colorClass] || colorMap.blue;
  };

  const getTrendingColor = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; icon: string }> = {
      green: { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-500' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-500' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: 'text-yellow-500' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-500' },
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex">
      {/* Sidebar */}
      <aside className={`bg-white border-r border-gray-200 shadow-lg transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <div className="flex items-center gap-2">
                <Image
                  src="/assets/images/intagg_logo-01-1770559550357.png"
                  alt="Intaggs Logo"
                  width={40}
                  height={40}
                  className="object-contain"
                  priority
                />
                <div>
                  <h3 className="font-bold text-lg text-red-500 leading-none">Intaggs</h3>
                  <p className="text-[10px] text-gray-600 leading-tight">news in 100 words</p>
                </div>
              </div>
            ) : (
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mx-auto">
                <Icon name="shield-check" className="w-6 h-6 text-blue-600" />
              </div>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {menuItems?.map((item) => (
              <Link
                key={item?.href}
                href={item?.href}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-600 transition-all group"
                title={!sidebarOpen ? item?.label : ''}
              >
                <Icon name={item?.icon} className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                {sidebarOpen && (
                  <span className="text-sm font-medium truncate">{item?.label}</span>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 rounded-lg transition-all"
          >
            <Icon name={sidebarOpen ? 'chevron-left' : 'chevron-right'} className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header with Logo and Tagline */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Dashboard Title */}
              <div className="flex items-center gap-4">
                <div className="border-l border-gray-300 pl-4">
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dashboard Overview</h1>
                  <p className="text-sm text-gray-600 mt-1">Welcome back, <span className="font-semibold text-gray-900">{user?.username}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Compact Search Button */}
                <div className="relative">
                  {showSearch ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
                        autoFocus
                      />
                      <button
                        onClick={() => setShowSearch(false)}
                        className="p-1.5 text-gray-500 hover:text-gray-700"
                      >
                        <Icon name="x-mark" className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowSearch(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                      title="Search"
                    >
                      <Icon name="magnifying-glass" className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Search</span>
                    </button>
                  )}
                </div>
                <Link
                  href="/homepage"
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="home" className="w-5 h-5" />
                  <span className="text-sm font-medium">Homepage</span>
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Icon name="arrow-right-on-rectangle" className="w-5 h-5" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex gap-6">
            {/* Left Content Area */}
            <div className="flex-1">
              {/* Stats Cards - Redesigned with Modern Layout */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Icon name="chart-bar" className="w-7 h-7 text-blue-600" />
                  Platform Statistics
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon name="users" className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-xs font-bold bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Active</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Users</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{stats?.totalUsers}</p>
                    <p className="text-xs text-gray-500">Registered accounts</p>
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon name="document-text" className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-xs font-bold bg-green-100 text-green-700 px-3 py-1 rounded-full">Published</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Articles</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{stats?.totalArticles}</p>
                    <p className="text-xs text-gray-500">Total content pieces</p>
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon name="folder" className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-xs font-bold bg-purple-100 text-purple-700 px-3 py-1 rounded-full">Active</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Categories</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{stats?.totalCategories}</p>
                    <p className="text-xs text-gray-500">Content categories</p>
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-orange-500 hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                        <Icon name="globe-alt" className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-xs font-bold bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Supported</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Countries</h3>
                    <p className="text-4xl font-bold text-gray-900 mb-1">{stats?.totalCountries}</p>
                    <p className="text-xs text-gray-500">Geographic coverage</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions Section - Enhanced Design */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Icon name="bolt" className="w-7 h-7 text-blue-600" />
                  Quick Actions
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {menuItems?.slice(0, 6)?.map((item) => {
                    const colors = getColorClasses(item.colorClass);
                    return (
                      <Link
                        key={item?.href}
                        href={item?.href}
                        className="bg-white rounded-2xl shadow-md p-6 border border-gray-200 hover:shadow-2xl hover:border-blue-300 transition-all transform hover:-translate-y-2 group"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-16 h-16 ${colors.bg} ${colors.hover} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all shadow-sm`}>
                            <Icon name={item?.icon} className={`w-8 h-8 ${colors.icon}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                              {item?.label}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{item?.description}</p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* System Overview Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activity Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Icon name="clock" className="w-6 h-6 text-blue-600" />
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Icon name="user-plus" className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">New user registered</p>
                        <p className="text-xs text-gray-600">2 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Icon name="document-plus" className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">Article published</p>
                        <p className="text-xs text-gray-600">15 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Icon name="pencil" className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">Category updated</p>
                        <p className="text-xs text-gray-600">1 hour ago</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Status Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Icon name="server" className="w-6 h-6 text-green-600" />
                    System Status
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-700">Database</span>
                      </div>
                      <span className="text-xs font-semibold text-green-600 bg-green-100 px-3 py-1 rounded-full">Operational</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-700">API Services</span>
                      </div>
                      <span className="text-xs font-semibold text-green-600 bg-green-100 px-3 py-1 rounded-full">Operational</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-700">Storage</span>
                      </div>
                      <span className="text-xs font-semibold text-green-600 bg-green-100 px-3 py-1 rounded-full">Operational</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-700">Cache</span>
                      </div>
                      <span className="text-xs font-semibold text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">Degraded</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Trending Now Section */}
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 sticky top-6">
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Icon name="fire" className="w-6 h-6" />
                    Trending Now
                  </h3>
                  <p className="text-xs text-blue-100 mt-1">Real-time platform insights</p>
                </div>
                <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="space-y-3">
                    {trendingItems?.map((item, index) => {
                      const colors = getTrendingColor(item.color);
                      return (
                        <div
                          key={index}
                          className={`${colors.bg} rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all cursor-pointer group`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}>
                              <Icon name={item.icon} className={`w-5 h-5 ${colors.icon}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold ${colors.text} mb-1 line-clamp-2`}>
                                {item.title}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600 font-medium">{item.category}</span>
                                <span className={`text-xs font-bold ${colors.text} bg-white px-2 py-0.5 rounded-full`}>
                                  {item.percentage}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Quick Stats Summary */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1">
                      <Icon name="sparkles" className="w-3.5 h-3.5" />
                      Quick Stats
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Icon name="eye" className="w-4 h-4 text-blue-500" />
                          <span className="text-xs font-medium text-gray-700">Page Views</span>
                        </div>
                        <span className="text-xs font-bold text-gray-900">12.4K</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Icon name="cursor-arrow-ripple" className="w-4 h-4 text-green-500" />
                          <span className="text-xs font-medium text-gray-700">Engagement</span>
                        </div>
                        <span className="text-xs font-bold text-gray-900">87%</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Icon name="clock" className="w-4 h-4 text-purple-500" />
                          <span className="text-xs font-medium text-gray-700">Avg. Time</span>
                        </div>
                        <span className="text-xs font-bold text-gray-900">4m 32s</span>
                      </div>
                    </div>
                  </div>

                  {/* Live Updates Indicator */}
                  <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-green-700">Live Updates Active</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">Data refreshes every 30 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}